import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listTimeslots,
  createTimeslot,
  updateTimeslot,
  deleteTimeslot,
  type TimeslotPreset,
} from "../api/timeslots";

// PHP weekday convention: 1=Sun … 7=Sat (matches app/services/timezone_svc.py).
const WEEKDAYS = [
  { num: 1, label: "Sun" },
  { num: 2, label: "Mon" },
  { num: 3, label: "Tue" },
  { num: 4, label: "Wed" },
  { num: 5, label: "Thu" },
  { num: 6, label: "Fri" },
  { num: 7, label: "Sat" },
];

function parseSlot(slot: string | null): { start: string; end: string } {
  const [start = "08:00", end = "21:00"] = (slot ?? "").split(",");
  return { start, end };
}

function daySet(weekday: string | null): Set<number> {
  return new Set(
    (weekday ?? "")
      .split(",")
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n)),
  );
}

function weekdayLabel(weekday: string | null): string {
  const set = daySet(weekday);
  if (set.size === 0) return "—";
  return WEEKDAYS.filter((d) => set.has(d.num))
    .map((d) => d.label)
    .join(" ");
}

export function TimeslotsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["timeslots"], queryFn: listTimeslots });
  const [editing, setEditing] = useState<TimeslotPreset | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => deleteTimeslot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["timeslots"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading calling windows…</p>;
  const presets = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Calling Windows</h1>
        <button
          onClick={() => setEditing("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New window
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Presets restrict campaigns to a local-time window on selected weekdays.
      </p>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Window</th>
              <th className="px-4 py-2">Days</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {presets.map((p) => {
              const { start, end } = parseSlot(p.allowed_timeslot);
              return (
                <tr key={p.timeslot_preset_id}>
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 font-mono">
                    {start}–{end}
                  </td>
                  <td className="px-4 py-2">{weekdayLabel(p.allowed_weekday)}</td>
                  <td className="px-4 py-2">{p.active ? "✓" : ""}</td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <button onClick={() => setEditing(p)} className="text-brand hover:underline">
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete window "${p.name}"?`)) remove.mutate(p.timeslot_preset_id);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {presets.length === 0 && <p className="text-gray-400 text-sm p-4">No calling windows defined.</p>}
      </div>

      {editing && (
        <Editor
          preset={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["timeslots"] });
          }}
        />
      )}
    </div>
  );
}

function Editor({
  preset,
  onClose,
  onSaved,
}: {
  preset: TimeslotPreset | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialSlot = parseSlot(preset?.allowed_timeslot ?? null);
  const [name, setName] = useState(preset?.name ?? "");
  const [start, setStart] = useState(initialSlot.start);
  const [end, setEnd] = useState(initialSlot.end);
  const [days, setDays] = useState<Set<number>>(
    preset ? daySet(preset.allowed_weekday) : new Set([2, 3, 4, 5, 6]),
  );
  const [active, setActive] = useState(preset?.active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        allowed_timeslot: `${start},${end}`,
        allowed_weekday: WEEKDAYS.filter((d) => days.has(d.num))
          .map((d) => d.num)
          .join(","),
        active,
      };
      return preset ? updateTimeslot(preset.timeslot_preset_id, body) : createTimeslot(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  function toggleDay(num: number) {
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num);
      else next.add(num);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <h2 className="text-lg font-bold">{preset ? "Edit" : "New"} calling window</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Start (local)</label>
            <input
              type="time"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">End (local)</label>
            <input
              type="time"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Days</label>
          <div className="flex gap-1">
            {WEEKDAYS.map((d) => (
              <button
                key={d.num}
                type="button"
                onClick={() => toggleDay(d.num)}
                className={`flex-1 rounded py-1.5 text-xs font-medium border ${
                  days.has(d.num)
                    ? "bg-brand text-white border-brand"
                    : "bg-white text-gray-600 border-gray-300"
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || days.size === 0 || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
